import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export type AppContext =
  | { kind: "personal"; id: "personal"; name: string }
  | { kind: "cafe"; id: string; name: string; slug?: string | null }
  | { kind: "club"; id: string; name: string; slug?: string | null };

const STORAGE_KEY = "brulo.activeContextId";

/* --- tiny external store so every mounted component stays in sync --- */
let activeId: string | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
const getSnapshot = () => activeId;
const getServerSnapshot = () => null;

export function setActiveContextId(id: string | null) {
  activeId = id;
  if (typeof window !== "undefined") {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  }
  emit();
}

/**
 * Contextos disponibles del usuario. Se calculan EXCLUSIVAMENTE a partir de
 * cafe_managers / club_managers (nunca desde profiles.role).
 */
export function useAppContexts() {
  const { user } = useAuth();
  const storedId = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (activeId === null) {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved) {
        activeId = saved;
        emit();
      }
    }
    setHydrated(true);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["app-contexts", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const [{ data: cafeLinks }, { data: clubLinks }] = await Promise.all([
        supabase
          .from("cafe_managers")
          .select("cafe_id, cafes(id,name,slug)")
          .eq("user_id", user!.id),
        supabase
          .from("club_managers")
          .select("club_id, clubs(id,name,slug)")
          .eq("user_id", user!.id),
      ]);

      const cafes: AppContext[] = (cafeLinks ?? [])
        .map((r: any) => r.cafes)
        .filter(Boolean)
        .map((c: any) => ({ kind: "cafe" as const, id: c.id, name: c.name, slug: c.slug }));

      const clubs: AppContext[] = (clubLinks ?? [])
        .map((r: any) => r.clubs)
        .filter(Boolean)
        .map((c: any) => ({ kind: "club" as const, id: c.id, name: c.name, slug: c.slug }));

      return [...cafes, ...clubs];
    },
  });

  const managed = data ?? [];
  const contexts = useMemo<AppContext[]>(
    () => [{ kind: "personal", id: "personal", name: "Personal" }, ...managed],
    [managed],
  );

  const active = useMemo<AppContext>(() => {
    const found = contexts.find((c) => c.id === storedId);
    if (found) return found;
    // Sin selección previa: mantiene el comportamiento actual (panel de gestión
    // si administra algo, si no, experiencia de atleta).
    return managed[0] ?? contexts[0];
  }, [contexts, managed, storedId]);

  const setActive = useCallback((id: string) => setActiveContextId(id), []);

  return {
    contexts,
    managed,
    active,
    setActive,
    // `ready` evita parpadeos de panel equivocado antes de conocer los vínculos
    ready: !!user && !isLoading && hydrated,
    loading: isLoading,
  };
}
